import { useEffect } from "react";
import { AlertCircle, CheckCircle2, Clock, Loader2, Mail, X } from "lucide-react";
import usePurchaseStatusStore from "../stores/purchaseStatusStore";

// A slim status pill at the top of the screen while a purchase lands, then its
// result. It never blocks the app: the player can keep playing underneath.
// Mounted once at the app root (see App.jsx); driven by Services/purchaseTracker.
const SUCCESS_VISIBLE_MS = 5000;

const TONES = {
  processing: {
    icon: <Loader2 size={18} className="animate-spin text-brand-accent" aria-hidden="true" />,
    ring: "border-brand-border",
  },
  success: {
    icon: <CheckCircle2 size={18} className="text-green-600" aria-hidden="true" />,
    ring: "border-green-200",
  },
  delayed: {
    icon: <Clock size={18} className="text-amber-600" aria-hidden="true" />,
    ring: "border-amber-200",
  },
  failed: {
    icon: <AlertCircle size={18} className="text-brand-accent" aria-hidden="true" />,
    ring: "border-red-200",
  },
};

function mailtoFor(email, reference) {
  const subject = "NameIt! purchase not received";
  const body = reference
    ? `Hi, I paid but my purchase hasn't arrived.\n\nPayment reference: ${reference}\n`
    : "Hi, I paid but my purchase hasn't arrived.\n";
  return `mailto:${email}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
}

const actionClass =
  "inline-flex items-center gap-1.5 h-8 px-3 rounded-lg text-xs font-semibold transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-brand-accent";

export default function PurchaseStatusBanner() {
  const { status, title, message, reference, contactEmail, clear } = usePurchaseStatusStore();

  useEffect(() => {
    if (status !== "success") return undefined;
    const timer = setTimeout(clear, SUCCESS_VISIBLE_MS);
    return () => clearTimeout(timer);
  }, [status, title, clear]);

  if (status === "idle") return null;
  const tone = TONES[status];

  return (
    // Sits just under the app's fixed 64px header, so the logo and coin balance
    // (which updates when the purchase lands) stay visible.
    <div className="pointer-events-none fixed inset-x-0 top-0 z-[100] flex justify-center px-4 pt-[calc(env(safe-area-inset-top,0px)+72px)]">
      <div
        key={`${status}:${title}`}
        role={status === "failed" ? "alert" : "status"}
        aria-live={status === "failed" ? "assertive" : "polite"}
        className={`purchase-banner pointer-events-auto flex w-full max-w-md items-start gap-3 rounded-2xl border ${tone.ring} bg-brand-surface/95 px-4 py-3 shadow-[0_8px_24px_-8px_rgba(31,41,55,0.25)] backdrop-blur-sm`}
      >
        <span className="mt-px flex-shrink-0">{tone.icon}</span>
        <div className="min-w-0 flex-1">
          <p className="text-sm font-semibold leading-5 text-brand-text">{title}</p>
          {message && <p className="mt-0.5 text-xs leading-5 text-brand-textMuted">{message}</p>}
          {reference && contactEmail && (
            <p className="mt-1 text-xs leading-5 text-brand-textSecondary [overflow-wrap:anywhere]">
              Reference <span className="font-mono text-brand-textMuted">{reference}</span>
            </p>
          )}
          {contactEmail && (
            <div className="mt-2">
              <a href={mailtoFor(contactEmail, reference)} className={`${actionClass} bg-brand-accent text-white hover:bg-red-700`}>
                <Mail size={14} aria-hidden="true" />
                Email us
              </a>
            </div>
          )}
        </div>
        <button
          type="button"
          onClick={clear}
          aria-label="Dismiss"
          className="-mr-1.5 -mt-0.5 flex-shrink-0 rounded-full p-1.5 text-brand-textSecondary transition-colors hover:bg-brand-surface2 hover:text-brand-text focus-visible:outline focus-visible:outline-2 focus-visible:outline-brand-accent"
        >
          <X size={16} aria-hidden="true" />
        </button>
      </div>
    </div>
  );
}
